/* eslint-disable array-bracket-newline, react-hooks/exhaustive-deps, indent, no-undef, no-unused-vars, react/jsx-key */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AutoSizer } from 'react-virtualized';
import { ListOnScrollProps, VariableSizeList } from 'react-window';
import {
  useTable,
  useSortBy,
  useBlockLayout,
  useResizeColumns,
  useRowState,
} from 'react-table';
import Loader from './Loader';
import ColumnsList from './ColumnsList';
import Image from '../../atoms/Image';
import Text from '../../atoms/Text';
import shevron_down from '../../assets/images/shevron_down.svg';
import arrow_right from '../../assets/images/arrow_right.svg';
import shevron_up from '../../assets/images/shevron_up.svg';
import download from '../../assets/images/download.svg';
import pencil from '../../assets/images/pencil.svg';
import useDownloadCSVData from './useDownloadCSVData';
import useUpscale from './useUpscale';
import SubTable, { SubTableColumns } from './SubTable';
import WatchlistWarning from '../../components/Watchlist/WatchlistWarning';

import 'react-virtualized/styles.css';
import './styles.scss';

export const scrollbarWidth = 21;
export const firstColumnGap = 9;
export const otherColumnsGap = 17;
const ROW_HEIGHT = 28;
const SUB_ROW_HEIGHT = 24;
const SUB_HEADER_HEIGHT = 32;
const SUB_TABLE_GAP = 24;

const getColumnWidth = (index: number, initialWidth: string) => {
  let width = Number(initialWidth.replace('px', ''));
  if (index) {
    width = width < otherColumnsGap ? otherColumnsGap : width;
  } else {
    width = width < firstColumnGap ? firstColumnGap : width;
  }
  return width;
};

export interface ITableSort {
  id: string;
  isSortedDesc: boolean;
}

export interface ITablePopup {
  text: string;
  buttonText: string;
  onSubmit(): void;
}

interface IDesktopTable {
  dataKey: string;
  title: string;
  tableColumnsWidth: { [key: string]: number };
  tableWidth: number;
  columns: any[];
  defaultSortedColumn?: { id: string; desc: boolean };
  data: any[];
  hiddenColumns: string[];
  hiddenColumnsKey?: string;
  csvData?: any[];
  loading?: boolean;
  placeholderText?: string;
  hoveredKey?: string;
  withTableControls?: boolean;
  withBorder?: boolean;
  sort?: ITableSort;
  rememberColWidth?: boolean;
  extendedRowId?: string;
  subColumns?: SubTableColumns;
  rowHeight?: number;
  popup?: ITablePopup;
  getClassNameForRow?(row: any): string;
  renderCell(cell: any, index: number): React.ReactNode;
  renderTableControls?(): JSX.Element;
  onRowClick?(row: any): void;
  onSubRowClick?(row: any): void;
  onScroll?(listHeight: number, scrollPos: number, isInitialPos: boolean): void;
  onSort?(id: string): void;
  renderSubCell?(
    accessor: string,
    parentRow: any,
    row: any,
    value: any
  ): JSX.Element;
}

const DesktopTable = ({
  hiddenColumns,
  dataKey,
  title,
  tableColumnsWidth,
  tableWidth,
  columns,
  defaultSortedColumn,
  data,
  placeholderText,
  hiddenColumnsKey = '',
  csvData,
  loading,
  hoveredKey,
  rememberColWidth = true,
  withTableControls = true,
  withBorder = true,
  sort,
  extendedRowId,
  subColumns,
  rowHeight = ROW_HEIGHT,
  popup,
  getClassNameForRow,
  renderCell,
  renderTableControls,
  onRowClick,
  onSubRowClick,
  onScroll,
  onSort,
  renderSubCell,
}: IDesktopTable) => {
  const tableHiddenLC = localStorage.getItem(hiddenColumnsKey) || 'null';
  const reducer = (newState: any, action: any) => {
    switch (action.type) {
      case 'fitMaxWidth':
        return {
          ...newState,
          columnResizing: {
            columnWidths: {
              ...newState.columnResizing.columnWidths,
              ...action.payload,
            },
          },
        };
      case 'hideColumns':
        return {
          ...newState,
          hiddenColumns: action.payload,
        };
      default:
        return newState;
    }
  };

  const [isHeaderEditable, setHeaderEditable] = useState(false);

  const [tableLoading, setTableLoading] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const contentsRef = useRef<HTMLDivElement>(null);
  const nativeListRef = useRef<VariableSizeList<any>>(null);

  const sizeMap = useRef<Record<number, number>>({});
  const setSize = useCallback((index, size) => {
    if (nativeListRef.current) {
      sizeMap.current = { ...sizeMap.current, [index]: size };
      nativeListRef.current.resetAfterIndex(index);
    }
  }, []);
  const resetSizes = useCallback(() => {
    sizeMap.current = {};
  }, []);
  const getSize = useCallback(
    (index: number) => sizeMap.current[index] || rowHeight,
    []
  );

  const sortees = useMemo(
    () => (defaultSortedColumn ? [defaultSortedColumn] : []),
    []
  );

  const hidees = useMemo(() => JSON.parse(tableHiddenLC) || hiddenColumns, []);

  const tableInstance = useTable(
    {
      columns,
      data,
      disableSortRemove: true,
      autoResetSortBy: false,
      initialState: {
        // @ts-ignore
        sortBy: sortees,
        hiddenColumns: hidees,
      },
      stateReducer: reducer,
    },
    useSortBy,
    useBlockLayout,
    useResizeColumns,
    useRowState
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    allColumns,
    visibleColumns,
    dispatch,
    state,
  } = tableInstance;

  const { upscale, handleUpscale, totalColumnsWidth } = useUpscale(
    tableWidth,
    columns,
    headerGroups,
    dispatch,
    dataKey,
    rememberColWidth
  );
  const { handleDownload } = useDownloadCSVData(title, csvData || []);

  const handleScroll = (
    { scrollOffset }: ListOnScrollProps,
    tableHeight: number
  ) => {
    if (listRef.current && onScroll) {
      const list = listRef.current;
      const listHeight = list.clientHeight;
      const scrollPos = scrollOffset + tableHeight;
      const isInitialPos = scrollPos === tableHeight;
      onScroll(listHeight, scrollPos, isInitialPos);
    }
  };

  useEffect(() => {
    const tableWidthsLC = localStorage.getItem(dataKey);
    if (!tableWidthsLC && tableWidth && headerGroups[0]) {
      handleUpscale();
    }
  }, [tableWidth]);

  useEffect(() => {
    if (tableWidth && headerGroups[0]) {
      handleUpscale();
    }
  }, [state.hiddenColumns?.length]);

  useEffect(() => {
    if (!hiddenColumnsKey) {
      dispatch({
        type: 'hideColumns',
        payload: hiddenColumns,
      });
    }
  }, [hiddenColumns]);

  // reset row sizes when data changes
  useEffect(() => {
    resetSizes();
  }, [data]);

  // save to localStorage if resizing ended
  useEffect(() => {
    if (
      (state as any).columnResizing.isResizingColumn === null &&
      rememberColWidth
    ) {
      let updated = tableColumnsWidth;

      Object.keys((state as any).columnResizing.columnWidths).forEach(
        (item) => {
          updated = {
            ...updated,
            [item]: (state as any).columnResizing.columnWidths[item],
          };
        }
      );

      localStorage.setItem(dataKey, JSON.stringify(updated));
    }
  }, [(state as any).columnResizing]);

  useEffect(() => {
    let timer: any;
    if (!loading) {
      timer = setTimeout(() => {
        setTableLoading(!!loading);
      }, 300);
    } else {
      setTableLoading(loading);
    }

    return () => window.clearTimeout(timer);
  }, [loading]);

  const renderRow = React.useCallback(
    ({ index, style }) => {
      const [extended, setExtended] = useState(getSize(index) !== rowHeight);
      if (popup && index === rows.length) {
        return (
          <div className='table_popup' style={style}>
            <WatchlistWarning
              width='auto'
              text={popup.text}
              buttonText={popup.buttonText}
              onSubmit={popup.onSubmit}
            />
          </div>
        );
      }
      const row: any = rows[index];
      prepareRow(row);
      return (
        <div
          {...row.getRowProps()}
          style={{
            ...style,
            flexDirection: 'column',
          }}
          className='tr'
          {...(hoveredKey
            ? {
                'data-hovered': row.original[hoveredKey],
              }
            : {})}
        >
          <div
            className={`tr_inner_wrapper ${
              getClassNameForRow ? getClassNameForRow(row.original) : ''
            }`}
            style={{
              minHeight: rowHeight,
              ...(onRowClick ? { cursor: 'pointer' } : {}),
            }}
            onClick={() => onRowClick && onRowClick(row.original)}
          >
            {row.cells.map((cell: any, cellIndex: any) => {
              const props = cell.getCellProps();
              props.style.minWidth = getColumnWidth(
                cellIndex,
                props.style.width
              );
              if (cell.column.id === 'extender' && extendedRowId) {
                return (
                  <div className='td' {...props} data-qa={props.key}>
                    {extendedRowId && !!row.original[extendedRowId]?.length && (
                      <div
                        className='extend'
                        onClick={(e) => {
                          e.stopPropagation();
                          if (extended) {
                            setSize(index, rowHeight);
                            setExtended(false);
                          } else {
                            const subRowsNum =
                              row.original[extendedRowId!].length;
                            const subRowHeight =
                              subRowsNum * SUB_ROW_HEIGHT +
                              SUB_HEADER_HEIGHT +
                              SUB_TABLE_GAP;
                            setSize(index, subRowHeight + rowHeight);
                            setExtended(true);
                          }
                        }}
                      >
                        <div
                          className={`extend__trigger icon-chevron-down ${
                            extended ? 'extend__trigger--active' : ''
                          }`}
                        />
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <div
                  className={`td ${withBorder ? 'td--with_border' : ''}`}
                  {...props}
                  data-qa={props.key}
                >
                  {renderCell(cell, index + 1)}
                </div>
              );
            })}
          </div>
          {extended && subColumns && extendedRowId && renderSubCell && (
            <div className='extended_row'>
              <SubTable
                columns={subColumns}
                data={row.original[extendedRowId]}
                parentRow={row.original}
                renderSubCell={renderSubCell}
                onRowClick={onSubRowClick}
              />
            </div>
          )}
        </div>
      );
    },
    [prepareRow, rows, renderCell, setSize, getSize]
  );

  /**
   * Handle hover logic
   */
  useEffect(() => {
    const contents = contentsRef.current;

    const onHighlight = (e: any) => {
      if (e.target && contents) {
        const closestRow = e.target.closest('.tr') as HTMLDivElement;
        if (closestRow) {
          const wallet = closestRow.dataset.hovered;
          const data = contents.dataset;
          if (data.lockedWallet !== wallet) {
            data.lockedWallet = wallet;
            const hoveredElements = contents.querySelectorAll('.hovered');
            const elementsToHover = contents.querySelectorAll(
              `[data-hovered='${wallet}']`
            );
            hoveredElements.forEach((element) => {
              element.classList.remove('hovered');
            });
            elementsToHover.forEach((element) => {
              element
                .querySelector('.tr_inner_wrapper')
                ?.classList.add('hovered');
            });
          }
        }
      }
    };

    const onRemoveHighlight = () => {
      if (contents) {
        contents.dataset.lockedWallet = '';
        const elements = contents.querySelectorAll('.hovered');
        elements.forEach((element) => {
          element.classList.remove('hovered');
        });
      }
    };

    if (contents && hoveredKey) {
      window.requestIdleCallback(() => {
        contents.addEventListener('mouseover', onHighlight);
        contents.addEventListener('mouseleave', onRemoveHighlight);
      });
    }

    return () => {
      if (contents && hoveredKey) {
        contents.removeEventListener('mouseover', onHighlight);
        contents.removeEventListener('mouseleave', onRemoveHighlight);
      }
    };
  });

  const dataCheck = !!(!loading && data.length);
  const controlsCheck = !!(
    withTableControls &&
    ((csvData && dataCheck) || upscale || renderTableControls)
  );

  return (
    <div className='table_desktop'>
      {isHeaderEditable && (
        <ColumnsList
          onHide={() => setHeaderEditable(false)}
          allColumns={allColumns}
          visibleColumns={visibleColumns}
          hiddenColumnsKey={hiddenColumnsKey}
          height={contentsRef.current?.clientHeight || 0}
        />
      )}
      {controlsCheck && (
        <div className='table_controls'>
          {dataCheck && (
            <Image
              className='btn'
              size='s'
              src={pencil}
              onClick={() => setHeaderEditable(!isHeaderEditable)}
              data-tip='Edit columns'
            />
          )}
          {csvData && dataCheck && (
            <Image
              className='btn'
              size='s'
              src={download}
              onClick={handleDownload}
              data-tip='Download'
            />
          )}
          {upscale && (
            <Image
              className='btn'
              size='s'
              src={arrow_right}
              onClick={handleUpscale}
              data-tip='Fit table to max width'
            />
          )}
          {renderTableControls && renderTableControls()}
        </div>
      )}
      <div className='table' {...getTableProps()}>
        <div className='thead'>
          {headerGroups.map((headerGroup) => (
            <div className='thr' {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column: any, index) => {
                const props = column.getHeaderProps(
                  column.getSortByToggleProps()
                );
                props.style.minWidth = getColumnWidth(index, props.style.width);
                const hasBorder =
                  withBorder && !(column.id === 'extender' && extendedRowId);
                return (
                  <div
                    className={`th ${hasBorder ? 'th--with_border' : ''}`}
                    key={JSON.stringify(column)}
                    {...props}
                    {...(onSort && !column.disableSortBy
                      ? { onClick: () => onSort(column.id) }
                      : {})}
                  >
                    <Text
                      size={12}
                      className={column.attachRight ? 'attach_right' : ''}
                      data-qa={`${column.Header}`}
                    >
                      {column.render('Header')}
                    </Text>
                    {!column.disableSortBy && !sort && (
                      <span>
                        <div className='img'>
                          {column.isSorted ? (
                            column.isSortedDesc ? (
                              <Image
                                src={shevron_down}
                                size='s'
                                data-qa={`down-${column.Header}`}
                              />
                            ) : (
                              <Image
                                src={shevron_up}
                                size='s'
                                data-qa={`up-${column.Header}`}
                              />
                            )
                          ) : null}
                        </div>
                      </span>
                    )}
                    {sort && !column.disableSortBy && (
                      <span>
                        <div className='img'>
                          {sort.id === column.id ? (
                            sort.isSortedDesc ? (
                              <Image
                                src={shevron_down}
                                size='s'
                                data-qa={`down-${column.Header}`}
                              />
                            ) : (
                              <Image
                                src={shevron_up}
                                size='s'
                                data-qa={`up-${column.Header}`}
                              />
                            )
                          ) : null}
                        </div>
                      </span>
                    )}
                    <div
                      {...column.getResizerProps()}
                      onClick={(e) => e.stopPropagation()}
                      className='resizer'
                    />
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        {tableLoading ? (
          <div className='centrify'>
            <Loader hide={!loading} />
          </div>
        ) : !data.length ? (
          <div className='centrify'>
            {popup ? (
              <div className='table_popup'>
                <WatchlistWarning
                  width='auto'
                  text={popup.text}
                  buttonText={popup.buttonText}
                  onSubmit={popup.onSubmit}
                />
              </div>
            ) : (
              placeholderText ?? 'No data found'
            )}
          </div>
        ) : (
          <div
            className='tbody'
            {...getTableBodyProps()}
            ref={contentsRef}
            {...(hoveredKey
              ? {
                  'data-locked-hovered': '',
                }
              : {})}
          >
            <AutoSizer>
              {({ height }) => (
                <VariableSizeList
                  ref={nativeListRef}
                  height={height}
                  itemCount={popup ? rows.length + 1 : rows.length}
                  itemSize={getSize}
                  width={totalColumnsWidth + scrollbarWidth}
                  className='table_insights'
                  innerRef={listRef}
                  onScroll={(e) => handleScroll(e, height)}
                >
                  {renderRow}
                </VariableSizeList>
              )}
            </AutoSizer>
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(DesktopTable);
